import React, { useState, useEffect, useRef } from 'react';
import { CoupleSettings, TimeElapsed, SongTrack, NavVisibilitySettings } from '../types';
import { AppTab } from '../App';
import { musicPlayer, PlayerState } from '../utils/musicPlayer';
import { subscribeToMusicTracks } from '../services/musicDb';
import {
  Heart,
  Music,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  ListMusic,
  Flower2,
  Camera,
  Mail,
  Sparkles,
  ChevronDown,
  X,
  Repeat,
  Shuffle,
  Disc,
  Check,
  Globe,
  Quote,
  KeyRound,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { RomanticMusicPlayerModal } from './RomanticMusicPlayerModal';

interface MinimalistHeaderProps {
  activeTab: AppTab;
  settings: CoupleSettings;
  timeElapsed: TimeElapsed;
  navVisibility: NavVisibilitySettings;
  isAdminAuthenticated: boolean;
  isCustomWeb?: boolean;
  onNavigate: (tab: AppTab) => void;
  onOpenAdminMusic?: () => void;
}

export const MinimalistHeader: React.FC<MinimalistHeaderProps> = ({
  activeTab,
  settings,
  timeElapsed,
  navVisibility,
  isAdminAuthenticated,
  isCustomWeb = false,
  onNavigate,
  onOpenAdminMusic,
}) => {
  const [playerState, setPlayerState] = useState<PlayerState>(() => musicPlayer.getState());
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);

  // Synchronize playlist from settings into musicPlayer
  useEffect(() => {
    if (isCustomWeb) {
      musicPlayer.setPlaylist(settings.playlist || [], settings.activeSongId);
      return;
    }
    if (settings.playlist && settings.playlist.length > 0) {
      musicPlayer.setPlaylist(settings.playlist, settings.activeSongId);
    }
  }, [settings.playlist, settings.activeSongId, isCustomWeb]);

  // Real-time listener to Firestore music_tracks collection (only for main site, not custom couple webs)
  useEffect(() => {
    if (isCustomWeb) return;
    const unsub = subscribeToMusicTracks((tracks) => {
      musicPlayer.setPlaylist(tracks || [], settings.activeSongId);
    });
    return () => unsub();
  }, [settings.activeSongId, isCustomWeb]);

  // Subscribe to music player state changes
  useEffect(() => {
    const unsub = musicPlayer.subscribe((state) => {
      setPlayerState(state);
    });
    return unsub;
  }, []);

  const playlist = isCustomWeb
    ? (settings.playlist || [])
    : ((playerState.playlist && playerState.playlist.length > 0)
      ? playerState.playlist
      : (settings.playlist || []));
  const currentTrack = playlist.length > 0 ? playerState.currentTrack : null;

  return (
    <>
      <header
        className="sticky top-0 z-40 transition-all duration-300 w-full select-none bg-white/90 text-[#302B30] backdrop-blur-2xl border-b border-[#F0DDE4] shadow-[0_4px_20px_rgba(232,93,142,0.06)]"
        id="minimalist-header"
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-2.5 sm:gap-4">
          {/* ======================================================== */}
          {/* 1. MODERN COUPLE BRANDING — ACCESO ADMIN POR CORAZÓN O NOMBRES */}
          {/* ======================================================== */}
          <div
            onClick={() => onNavigate('admin')}
            className="flex items-center gap-2 sm:gap-3 cursor-pointer group shrink-0"
            id="minimalist-brand"
            title="Acceso al Panel de Administración"
          >
            {/* Isotipo con aura de cristal */}
            <div className="relative flex items-center justify-center shrink-0">
              <div className="w-8.5 h-8.5 sm:w-9.5 sm:h-9.5 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-105 shrink-0 bg-gradient-to-tr from-[#E85D8E] via-[#E83E78] to-amber-400 p-[1.5px] shadow-[0_2px_14px_rgba(232,93,142,0.25)]">
                <div className="w-full h-full rounded-full flex items-center justify-center bg-white text-[#E83E78]">
                  <Heart className="w-4 h-4 fill-[#E83E78] text-[#E83E78] animate-pulse" />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
              <div className="flex flex-col shrink-0 leading-tight">
                <span className="font-['Playfair_Display',serif] text-xs xs:text-sm sm:text-base font-bold tracking-tight whitespace-nowrap transition-colors text-[#302B30] group-hover:text-[#E83E78]">
                  Nuestros Recuerdos
                </span>
                <span className="text-[10px] text-[#77717A] font-medium tracking-wide whitespace-nowrap hidden sm:inline">
                  Un espacio para guardar nuestra historia ❤️
                </span>
              </div>
            </div>
          </div>

          {/* ======================================================== */}
          {/* 2. MODERN FLOATING GLASS TABS (Desktop / Tablet) */}
          {/* ======================================================== */}
          <nav
            className="hidden sm:flex items-center p-1 rounded-full border border-[#F0DDE4] bg-[#F8F5F7] backdrop-blur-md shadow-2xs"
            id="minimalist-nav-tabs"
          >
            {[
              { id: 'garden' as const, label: 'Jardín', icon: Flower2, visible: navVisibility.garden },
              { id: 'blog' as const, label: 'Galería', icon: Camera, visible: navVisibility.blog },
              { id: 'letters' as const, label: 'Cartas', icon: Mail, visible: navVisibility.letters },
              { id: 'animations' as const, label: 'Frases', icon: Quote, iconClass: 'rotate-180', visible: navVisibility.animations },
              { id: 'myweb' as const, label: 'Mi web', icon: Globe, visible: !isCustomWeb && navVisibility.myWeb !== false && navVisibility.myweb !== false },
              { id: 'admin' as const, label: 'Mi Admin', icon: KeyRound, visible: Boolean(isCustomWeb) },
            ]
              .filter((item) => item.visible)
              .map((item) => {
                const isActive = activeTab === item.id;
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onNavigate(item.id)}
                    className={`relative px-3.5 py-1.5 text-xs font-semibold rounded-full transition-colors flex items-center gap-1.5 cursor-pointer select-none group ${
                      isActive
                        ? activeTab === 'garden'
                          ? 'text-slate-950 font-bold'
                          : 'text-white font-bold'
                        : activeTab === 'garden'
                        ? 'text-white/70 hover:text-white'
                        : 'text-slate-600 hover:text-slate-950'
                    }`}
                    id={`header-tab-${item.id}`}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="desktop-header-active-pill"
                        className="absolute inset-0 rounded-full shadow-xs -z-0 bg-gradient-to-r from-[#E85D8E] to-[#E83E78] shadow-[0_2px_12px_rgba(232,62,120,0.35)]"
                        transition={{ type: 'spring', stiffness: 450, damping: 35 }}
                      />
                    )}
                    <span className="relative z-10 flex items-center gap-1.5">
                      <Icon className={`w-3.5 h-3.5 ${item.iconClass || ''}`} />
                      <span>{item.label}</span>
                    </span>
                  </button>
                );
              })}
          </nav>

          {/* ======================================================== */}
          {/* 3. CAPSULE MUSIC DOCK & QUICK CONTROLS */}
          {/* ======================================================== */}
          <div className="flex items-center gap-1 sm:gap-2 min-w-0 shrink-0">
            {/* Dynamic Capsule Music Player */}
            <div
              className={`flex items-center gap-0.5 xs:gap-1 sm:gap-1.5 px-1.5 xs:px-2 py-1 sm:py-1.5 rounded-full border transition-all select-none shadow-xs min-w-0 ${
                playerState.isPlaying
                  ? 'bg-white border-[#E85D8E] text-[#302B30] shadow-[0_4px_16px_rgba(232,93,142,0.15)]'
                  : 'bg-white/90 border-[#F0DDE4] text-[#77717A] hover:text-[#302B30] hover:bg-white'
              }`}
              id="minimalist-music-dock"
            >
              {/* Quick Skip Previous Track Button (Devolver canción) */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  musicPlayer.previousTrack();
                }}
                className="w-6.5 h-6.5 sm:w-7 sm:h-7 rounded-full flex items-center justify-center transition-all cursor-pointer active:scale-90 shrink-0 hover:bg-[#FFF4F7] text-[#77717A] hover:text-[#E83E78]"
                title="Canción anterior"
                aria-label="Canción anterior"
              >
                <SkipBack className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-current" />
              </button>

              {/* Play / Pause Toggle Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (playlist.length === 0) {
                    setIsPlayerModalOpen(true);
                  } else {
                    musicPlayer.togglePlay();
                  }
                }}
                className={`w-7.5 h-7.5 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-xs active:scale-95 shrink-0 ${
                  playerState.isPlaying
                    ? 'bg-gradient-to-tr from-[#E85D8E] to-[#E83E78] text-white scale-105 shadow-[0_2px_10px_rgba(232,62,120,0.35)]'
                    : 'bg-[#FFF4F7] text-[#E83E78] hover:bg-[#FFE6EE] border border-[#F0DDE4]'
                }`}
                title={playlist.length === 0 ? 'Sin música (abrir reproductor)' : (playerState.isPlaying ? 'Pausar música' : 'Reproducir música')}
                aria-label={playerState.isPlaying ? 'Pausar' : 'Reproducir'}
              >
                {playerState.isPlaying ? (
                  <Pause className="w-3.5 h-3.5 fill-current" />
                ) : (
                  <Play className="w-3.5 h-3.5 fill-current ml-0.5" />
                )}
              </button>

              {/* Quick Skip Next Track Button (Pasar canción) */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  musicPlayer.nextTrack();
                }}
                className="w-6.5 h-6.5 sm:w-7 sm:h-7 rounded-full flex items-center justify-center transition-all cursor-pointer active:scale-90 shrink-0 hover:bg-[#FFF4F7] text-[#77717A] hover:text-[#E83E78]"
                title="Siguiente canción"
                aria-label="Siguiente canción"
              >
                <SkipForward className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-current" />
              </button>

              {/* Song Title & Dynamic Equalizer (Click opens full romantic player) */}
              <button
                type="button"
                onClick={() => setIsPlayerModalOpen(true)}
                className="flex items-center gap-1.5 cursor-pointer min-w-0 max-w-[115px] xs:max-w-[155px] sm:max-w-[210px] md:max-w-[270px] group px-1 text-left border-l border-[#F0DDE4] pl-1.5 ml-0.5"
                title={currentTrack ? `${currentTrack.title} • ${currentTrack.artist || ''} • Clic para abrir el reproductor` : 'Reproductor de música'}
              >
                {/* 3 Dynamic Audio Equalizer Bars */}
                <div className="flex items-end gap-[2px] h-3.5 shrink-0" aria-hidden="true">
                  <span
                    className={`w-[2px] rounded-full transition-all duration-300 ${
                      playerState.isPlaying
                        ? 'bg-[#E85D8E] [animation:audioBarBounce1_0.8s_ease-in-out_infinite]'
                        : 'bg-current opacity-30 h-1'
                    }`}
                  />
                  <span
                    className={`w-[2px] rounded-full transition-all duration-300 ${
                      playerState.isPlaying
                        ? 'bg-[#E83E78] [animation:audioBarBounce2_0.7s_ease-in-out_infinite]'
                        : 'bg-current opacity-30 h-2'
                    }`}
                  />
                  <span
                    className={`w-[2px] rounded-full transition-all duration-300 ${
                      playerState.isPlaying
                        ? 'bg-[#E85D8E] [animation:audioBarBounce3_0.9s_ease-in-out_infinite]'
                        : 'bg-current opacity-30 h-1.5'
                    }`}
                  />
                </div>

                <div className="flex flex-col min-w-0 leading-tight">
                  <span className="text-[11px] sm:text-xs font-bold truncate tracking-tight text-[#302B30] group-hover:text-[#E83E78] transition-colors">
                    {currentTrack ? currentTrack.title : (playlist.length === 0 ? 'Sin música' : 'Pausado')}
                  </span>
                  <span className="text-[9px] sm:text-[10px] text-[#77717A] truncate font-medium">
                    {currentTrack?.artist || (playlist.length === 0 ? 'Abrir lista' : 'Romántica')}
                  </span>
                </div>
              </button>

              {/* Playlist Button */}
              <button
                type="button"
                onClick={() => setIsPlayerModalOpen(true)}
                className="relative p-1.5 rounded-full transition-all cursor-pointer flex items-center justify-center shrink-0 hover:bg-[#FFF4F7] text-[#77717A] hover:text-[#E83E78]"
                title="Ver lista y reproductor"
                aria-label="Ver lista y reproductor"
              >
                <ListMusic className="w-3.5 h-3.5" />
                {playlist.length > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-1 text-[9px] font-black rounded-full bg-gradient-to-r from-[#E85D8E] to-[#E83E78] text-white flex items-center justify-center shadow-xs">
                    {playlist.length}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ======================================================== */}
      {/* 4. SOPHISTICATED FULL ROMANTIC PLAYER MODAL */}
      {/* ======================================================== */}
      <RomanticMusicPlayerModal
        isOpen={isPlayerModalOpen}
        onClose={() => setIsPlayerModalOpen(false)}
        playerState={playerState}
        playlist={playlist}
        settings={settings}
        onOpenAdminMusic={onOpenAdminMusic}
      />
    </>
  );
};
